import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { Button, ButtonGroup, CircularProgress, Tooltip } from '@mui/material'

import { Billing, BillingService } from 'generated'
import useConfig from 'hooks/useConfig'
import useToast from 'hooks/useToast'

import { Attribution, Response } from 'types'

import {
  AiIcon,
  FirstAttribution,
  LastAttribution,
  LinearIcon,
} from 'assets/icons'

const AttributionButton = ({ value, setValue }: Props) => {
  const [isAiAvailable, setIsAiAvailable] = useState(false)
  const queryClient = useQueryClient()
  const { workspace } = useConfig()
  const { toast } = useToast()

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchBillingInfo = async () => {
      setLoading(true)

      try {
        const billingData = await queryClient.ensureQueryData({
          queryKey: ['billing', workspace],
          queryFn: () =>
            BillingService.getBilling(workspace) as Response<Billing>,
        })

        const { is_ai_available } = billingData?.data?.subscription || {}
        setIsAiAvailable(is_ai_available || false)
      } catch (error) {
        toast({
          message: 'Fetching Error',
          alertColor: 'error',
        })
      } finally {
        setLoading(false)
      }
    }

    fetchBillingInfo()
  }, [workspace, queryClient, toast])

  useEffect(() => {
    if (!value) setValue('first')
    if (value === 'ai' && !isAiAvailable) setValue('first')
  }, [value, setValue, isAiAvailable])

  if (loading) {
    return (
      <CircularProgress
        sx={{ m: 1 }}
        size={20}
        thickness={5}
        color="inherit"
      />
    )
  }

  const attributionButtonSx = {
    minWidth: 36,
    width: 36,
    height: 32,
    maxHeight: 32,
    px: 0.5,
    py: 0,
    '& .MuiButton-startIcon': {
      m: 0,
    },
  }

  return (
    <ButtonGroup
      size="small"
      sx={{
        '& .MuiButton-root': {
          borderColor: 'primary.light',
        },
      }}>
      <Tooltip title="First Attribution">
        <Button
          startIcon={
            <FirstAttribution
              width={18}
              height={18}
            />
          }
          variant={value === 'first' ? 'contained' : 'outlined'}
          onClick={() => setValue('first')}
          sx={attributionButtonSx}
        />
      </Tooltip>
      <Tooltip title="Last Attribution">
        <Button
          startIcon={
            <LastAttribution
              width={18}
              height={18}
            />
          }
          variant={value === 'last' ? 'contained' : 'outlined'}
          onClick={() => setValue('last')}
          sx={attributionButtonSx}
        />
      </Tooltip>
      <Tooltip title="Linear Attbution">
        <Button
          startIcon={
            <LinearIcon
              height={18}
              width={18}
            />
          }
          variant={value === 'linear' ? 'contained' : 'outlined'}
          onClick={() => setValue('linear')}
          sx={attributionButtonSx}
        />
      </Tooltip>
      {isAiAvailable ? (
        <Tooltip title="Machine Learning Attribution">
          <Button
            startIcon={
              <AiIcon
                height={18}
                width={18}
              />
            }
            variant={value === 'ai' ? 'contained' : 'outlined'}
            onClick={() => setValue('ai')}
            sx={attributionButtonSx}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Not enough data">
          <div>
            <Button
              startIcon={
                <AiIcon
                  height={18}
                  width={18}
                />
              }
              disabled
              variant="outlined"
              sx={attributionButtonSx}
            />
          </div>
        </Tooltip>
      )}
    </ButtonGroup>
  )
}

interface Props {
  setValue: (param: Attribution) => void
  value?: Attribution
}

export default AttributionButton
